<template>
    <div class="AcceptanceReviewContent">
        <div class="AcceptanceReviewContent__container">
            <Table class="AcceptanceReviewContent__table">
                <template #head>
                    <HeadCell class="AcceptanceReviewContent__fieldHeader">
                        Field
                    </HeadCell>
                    <HeadCell class="AcceptanceReviewContent__valueHeader">
                        Value
                    </HeadCell>
                    <HeadCell class="AcceptanceReviewContent__decisionHeader">
                        Decision
                    </HeadCell>
                </template>
                <Row :align-top="alignTop" hoverable>
                    <Cell class="AcceptanceReviewContent__field">
                        {{ fieldName }}
                    </Cell>
                    <Cell class="AcceptanceReviewContent__value">
                        <span v-if="actualValue != null && actualValue !==''">
                            {{ actualValue }}
                        </span>
                        <EmptyValueDashSymbol v-else />
                        <slot name="requiredValue">
                            <div v-if="requiredValue" class="AcceptanceReviewContent__valueError">
                                <FontAwesomeIcon :icon="faExclamationTriangle" />
                                <span>"{{ requiredValue }}"</span>
                                <span> required.</span>
                            </div>
                        </slot>
                    </Cell>
                    <Cell class="AcceptanceReviewContent__decisionHeader">
                        <!-- A decided action keeps this whole layout and shows what was decided in
                             place of the controls, so the record reads like the screen it came from. -->
                        <span v-if="readOnly" class="AcceptanceReviewContent__recordedDecision">
                            {{ recordedDecision }}
                        </span>
                        <RbacDisableDefaultGuard v-else :features="permissionsToResolve">
                            <div class="AcceptanceReviewContent__decisionsContainer">
                                <NewButton
                                    class="AcceptanceReviewContent__rejectButton"
                                    variant="outline"
                                    color="danger"
                                    @click="$emit('reject')"
                                >
                                    Reject
                                </NewButton>
                                <NewButton
                                    class="AcceptanceReviewContent__acceptButton"
                                    variant="outline"
                                    color="primary"
                                    @click="$emit('accept')"
                                >
                                    Accept
                                </NewButton>
                            </div>
                        </RbacDisableDefaultGuard>
                    </Cell>
                </Row>
            </Table>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Cell, HeadCell, Row, Table } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisableDefaultGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableDefaultGuard.vue';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import EmptyValueDashSymbol
        from '@/modules/entity-actions-review/components/EmptyValueDashSymbol/EmptyValueDashSymbol.vue';

    export default defineComponent({
        name: 'AcceptanceReviewContent',
        components: {
            NewButton,
            Cell,
            EmptyValueDashSymbol,
            FontAwesomeIcon,
            HeadCell,
            RbacDisableDefaultGuard,
            Row,
            Table,
        },
        props: {
            fieldName: {
                type: String as PropType<string>,
                required: true,
            },
            actualValue: {
                type: String as PropType<string | null | undefined>,
                default: undefined,
            },
            requiredValue: {
                type: String as PropType<string>,
                default: undefined,
            },
            isGlobalType: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            alignTop: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            readOnly: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            // What was decided, shown in the Decision cell instead of the controls. Null when the
            // action predates the resolution record, where the decision was never stored.
            outcome: {
                type: String as PropType<'accepted' | 'rejected' | null>,
                default: null,
            },
        },
        emits: [ 'accept', 'reject' ],
        data() {
            return {
                faExclamationTriangle,
            };
        },
        computed: {
            recordedDecision(): string {
                if (this.outcome === 'accepted') {
                    return 'Accepted';
                }
                return this.outcome === 'rejected' ? 'Rejected' : 'Not recorded';
            },
            permissionsToResolve() {
                return this.isGlobalType
                    ? [ RbacFeature.actionsResolveGlobalAction ]
                    : [ RbacFeature.actionsResolveEntityAction ];
            },
        },
    });
</script>
